import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import supabase from '../../../supabase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';

const { 
  FiShield, FiAward, FiCheckBadge, FiStar, FiBriefcase 
} = FiIcons;

const QUALIFICATION_ICONS = {
  FiShield: FiShield,
  FiCheckBadge: FiCheckBadge,
  FiStar: FiStar,
  FiAward: FiAward,
  FiBriefcase: FiBriefcase
};

const UtrederPublicProfile = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { utredere } = useData();
  const [utreder, setUtreder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchUtreder = async () => {
      try {
        setLoading(true);
        console.log("Fetching utreder for slug:", slug);

        // First try to find in context (mock data or already loaded)
        // Fix: Resolve by slug OR id to support preview links from editor
        const foundInContext = utredere.find(u => u.slug === slug || String(u.id) === String(slug));
        if (foundInContext) {
          console.log("Found in context:", foundInContext);
          setUtreder(foundInContext);
          setLoading(false);
          return;
        }

        // If not in context, try to fetch from Supabase
        console.log("Not in context, fetching from Supabase...");
        const { data, error } = await supabase
          .from('utredere')
          .select('*')
          .or(`slug.eq.${slug},id.eq.${slug}`)
          .single();

        if (error) throw error;
        
        if (data) {
          console.log("Found in Supabase:", data);
          setUtreder(data);
        } else {
          setError("Fant ikke utreder");
        }
      } catch (err) {
        console.error("Error fetching utreder:", err);
        setError("Kunne ikke laste utrederprofilen");
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchUtreder();
    }
  }, [slug, utredere]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !utreder) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <SafeIcon icon={FiIcons.FiAlertTriangle} className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Fant ikke profil</h2>
          <p className="text-gray-600 mb-6">Beklager, vi kunne ikke finne utrederprofilen du ser etter.</p>
          <button 
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Gå til forsiden
          </button>
        </div>
      </div>
    );
  }

  // Parse arrays if they are strings (JSON from Supabase)
  const parseArray = (arr) => {
    if (Array.isArray(arr)) return arr;
    if (typeof arr === 'string') {
      try {
        return JSON.parse(arr);
      } catch (e) {
        return [];
      }
    }
    return [];
  };

  const competenceTags = parseArray(utreder.competence_tags);
  const certifications = parseArray(utreder.certifications);
  const languages = parseArray(utreder.languages);
  const qualifications = parseArray(utreder.qualifications);

  // Default branding colors
  const primaryColor = utreder.branding?.primaryColor || '#1e40af'; // blue-800
  const accentColor = utreder.branding?.accentColor || '#3b82f6'; // blue-500

  return (
    <div className="min-h-screen bg-gray-50 font-sans">
      {/* 3. UX REPARASJON: Legg til Tilbakeknapp - KORRIGERT ROUTE */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <Link 
            to="/company/dashboard" 
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-blue-600 transition-colors"
          >
            <SafeIcon icon={FiIcons.FiArrowLeft} className="mr-2" />
            ← Tilbake til Finn Utreder
          </Link>
        </div>
      </div>

      {/* Header / Hero Section */}
      <div 
        className="relative bg-blue-900 text-white pb-24 pt-12"
        style={{ backgroundColor: primaryColor }}
      >
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center md:items-start gap-8">
            {/* Profile Image / Initials */}
            <div className="flex-shrink-0">
              {utreder.branding?.logoUrl ? (
                <img 
                  src={utreder.branding.logoUrl} 
                  alt={utreder.full_name} 
                  className="w-32 h-32 rounded-full border-4 border-white shadow-lg object-cover bg-white"
                />
              ) : (
                <div className="w-32 h-32 rounded-full border-4 border-white shadow-lg bg-white flex items-center justify-center text-4xl font-bold text-gray-400">
                  {utreder.full_name?.substring(0, 2).toUpperCase()}
                </div>
              )}
            </div>
            
            {/* Name and Title */}
            <div className="text-center md:text-left flex-1">
              {/* Qualification Badges in Hero */}
              {qualifications.length > 0 && (
                <div className="flex flex-wrap justify-center md:justify-start gap-2 mb-3">
                  {qualifications.map((q, i) => (
                    <span key={i} className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5 border border-white/20">
                      <SafeIcon icon={QUALIFICATION_ICONS[q.icon] || FiAward} />
                      {q.label}
                    </span>
                  ))}
                </div>
              )}

              <h1 className="text-3xl md:text-4xl font-bold mb-2">{utreder.full_name}</h1>
              <p className="text-xl text-blue-100 mb-4">{utreder.title_role || 'Uavhengig Utreder'}</p>
              
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-800 bg-opacity-50 text-white border border-blue-400">
                  <SafeIcon icon={FiIcons.FiMapPin} className="mr-1.5 h-4 w-4" />
                  {utreder.geography || 'Norge'}
                </span>
                {utreder.company_name && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-800 bg-opacity-50 text-white border border-blue-400">
                    <SafeIcon icon={FiIcons.FiBriefcase} className="mr-1.5 h-4 w-4" />
                    {utreder.company_name}
                  </span>
                )}
              </div>
            </div>

            {/* Contact Card (Desktop) */}
            <div className="hidden md:block bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 border border-blue-400 border-opacity-30 min-w-[250px]">
              <h3 className="font-semibold mb-3 flex items-center">
                <SafeIcon icon={FiIcons.FiMessageSquare} className="mr-2" /> Kontaktinfo
              </h3>
              {utreder.public_contact_email && (
                <div className="mb-2 text-sm flex items-center">
                  <SafeIcon icon={FiIcons.FiMail} className="mr-2 opacity-70" />
                  <a href={`mailto:${utreder.public_contact_email}`} className="hover:underline">{utreder.public_contact_email}</a>
                </div>
              )}
              {utreder.public_contact_phone && (
                <div className="text-sm flex items-center">
                  <SafeIcon icon={FiIcons.FiPhone} className="mr-2 opacity-70" />
                  <a href={`tel:${utreder.public_contact_phone}`} className="hover:underline">{utreder.public_contact_phone}</a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      {/* 1. UX REPARASJON: Fjernet -mt-12 for å unngå overlapping med hero */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column (Main Info) */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* About Section */}
            <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3" style={{ color: primaryColor, backgroundColor: `${primaryColor}15` }}>
                  <SafeIcon icon={FiIcons.FiUser} />
                </span>
                Om meg
              </h2>
              <div className="prose text-gray-600 max-w-none">
                {utreder.bio ? (
                  <p className="whitespace-pre-line">{utreder.bio}</p>
                ) : (
                  <p className="italic text-gray-400">Ingen beskrivelse tilgjengelig.</p>
                )}
              </div>
            </div>

            {/* Experience Section */}
            <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mr-3" style={{ color: primaryColor, backgroundColor: `${primaryColor}15` }}>
                  <SafeIcon icon={FiIcons.FiAward} />
                </span>
                Erfaring og Kompetanse
              </h2>
              <div className="prose text-gray-600 max-w-none">
                {utreder.experience_summary ? (
                  <p className="whitespace-pre-line mb-6">{utreder.experience_summary}</p>
                ) : null}
                
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">Nøkkelkompetanse</h3>
                  <div className="flex flex-wrap gap-2">
                    {competenceTags.length > 0 ? (
                      competenceTags.map((tag, index) => (
                        <span key={index} className="inline-flex items-center px-3 py-1 rounded-md text-sm font-medium bg-gray-100 text-gray-800">
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="text-gray-500 italic">Ikke spesifisert</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Certifications Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {certifications.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <SafeIcon icon={FiIcons.FiCheckCircle} className="mr-2 text-green-500" />
                    Sertifiseringer
                  </h2>
                  <ul className="space-y-2">
                    {certifications.map((cert, index) => (
                      <li key={index} className="flex items-start text-gray-600">
                        <span className="mr-2 mt-1.5 w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0"></span>
                        {cert}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {languages.length > 0 && (
                <div className="bg-white rounded-lg shadow-sm p-6 md:p-8">
                  <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center">
                    <SafeIcon icon={FiIcons.FiGlobe} className="mr-2 text-blue-500" />
                    Språk
                  </h2>
                  <ul className="space-y-2">
                    {languages.map((lang, index) => (
                      <li key={index} className="flex items-start text-gray-600">
                        <span className="mr-2 mt-1.5 w-1.5 h-1.5 bg-gray-400 rounded-full flex-shrink-0"></span>
                        {lang}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

          </div>

          {/* Right Column (Sidebar) */}
          <div className="space-y-6">
            
            {/* Contact Card (Mobile Only - duplicate of header but visible on mobile) */}
            <div className="md:hidden bg-white rounded-lg shadow-sm p-6 border-t-4 border-blue-600" style={{ borderColor: primaryColor }}>
              <h3 className="font-bold text-gray-900 mb-4 text-lg">Kontaktinformasjon</h3>
              <div className="space-y-3">
                {utreder.public_contact_email && (
                  <div className="flex items-center text-gray-600">
                    <SafeIcon icon={FiIcons.FiMail} className="mr-3 text-gray-400" />
                    <a href={`mailto:${utreder.public_contact_email}`} className="hover:text-blue-600 truncate">{utreder.public_contact_email}</a>
                  </div>
                )}
                {utreder.public_contact_phone && (
                  <div className="flex items-center text-gray-600">
                    <SafeIcon icon={FiIcons.FiPhone} className="mr-3 text-gray-400" />
                    <a href={`tel:${utreder.public_contact_phone}`} className="hover:text-blue-600">{utreder.public_contact_phone}</a>
                  </div>
                )}
                {utreder.company_name && (
                  <div className="flex items-center text-gray-600">
                    <SafeIcon icon={FiIcons.FiBriefcase} className="mr-3 text-gray-400" />
                    <span>{utreder.company_name}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Price Indication */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                <SafeIcon icon={FiIcons.FiDollarSign} className="mr-2 text-gray-400" />
                Prisindikasjon
              </h3>
              <p className="text-gray-600 text-sm">
                {utreder.price_indication || 'Ta kontakt for et uforpliktende tilbud basert på sakens omfang og kompleksitet.'}
              </p>
            </div>

            {/* CTA Box */}
            <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 text-center">
              <h3 className="font-bold text-gray-900 mb-2">Ønsker du bistand?</h3>
              <p className="text-sm text-gray-600 mb-4">
                Ta kontakt direkte via e-post eller telefon for en konfidensiell samtale.
              </p>
              {utreder.public_contact_email && (
                /* 2. UX REPARASJON: Økt kontrast på knappen ved å bruke hvit tekst på tema-bakgrunn */
                <a 
                  href={`mailto:${utreder.public_contact_email}`}
                  className="block w-full py-2 px-4 text-white rounded-md hover:opacity-90 transition-opacity font-medium shadow-sm mb-2"
                  style={{ backgroundColor: accentColor }}
                >
                  Send e-post
                </a>
              )}
            </div>

            {/* Disclaimer */}
            <div className="text-xs text-gray-400 text-center mt-4">
              <p>Profil levert av HMSvarsling.no</p>
              <p>Utreder opererer som uavhengig tredjepart.</p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default UtrederPublicProfile;